import styled from "styled-components";

export const StyledHeader = styled.header`
  position: fixed;
  left: 0;
  right: 0;
  top: 0;
  z-index: 20;
  display: flex;
  align-items: center;
  justify-content: space-between;
  background-color: rgb(255, 255, 255);
  padding: 16px;
  transition-property:
    color, background-color, border-color, text-decoration-color, fill, stroke;
  transition-duration: 0.3s;
  transition-timing-function: cubic-bezier(0.4, 0, 0.2, 1);
  font-family: Nunito, "sans-serif";
  font-size: 16px;
  line-height: 24px;
  color: rgb(32, 33, 36);
  border: 0px solid rgb(32, 33, 36);
  box-sizing: border-box;

  @media (min-width: 1024px) {
    padding: 14px 32px;
  }
`;

export const StyledHeaderInner = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
`;

interface StyledHeaderNavProps {
  show?: boolean;
}

export const StyledHeaderNav = styled.nav<StyledHeaderNavProps>`
  display: none;
  font-family: Nunito, "sans-serif";
  font-size: 16px;
  font-weight: 700;
  line-height: 22px;

  @media (min-width: 1024px) {
    display: block;

    ul {
      display: inline-flex;
      align-items: center;
      gap: 1rem;
      list-style: none;
      margin: 0;
      padding: 0;
    }

    a {
      display: inline-block;
      padding: 12px;
      text-decoration: none;
      font-weight: 700;
      letter-spacing: -0.32px;
      font-size: 16px;
      line-height: 22px;
      color: rgb(32, 33, 36);
      cursor: pointer;
      transition-property: color;
      transition-duration: 0.3s;
      transition-timing-function: ease;
      font-family: Nunito, "sans-serif";
      box-sizing: border-box;
      &.selected-menu,
      &:hover {
        color: #ff5a6a;
      }
      &.custom-gray {
        color: rgb(32, 33, 36);
      }
    }
  }

  @media (min-width: 1280px) {
    ul {
      gap: 1.5rem;
    }
  }

  /* Mobile menu styles */
  @media (max-width: 1023px) {
    display: ${({ show }) => (show ? "flex" : "none")};
    position: fixed;
    top: 70px;
    left: 0;
    right: 0;
    bottom: 0;
    background-color: #fff;
    z-index: 999;
    flex-direction: column;
    align-items: flex-start;
    padding: 24px 32px;
    overflow-y: auto;

    ul {
      display: flex;
      flex-direction: column;
      list-style: none;
      margin: 0;
      padding: 0;
      width: 100%;
    }

    ul a {
      display: block;
      width: 100%;
      padding: 16px 0;
      border-bottom: 1px solid #eee;
      text-decoration: none;
      font-weight: 700;
      font-size: 16px;
      line-height: 22px;
      color: #5f6368;
      transition: color 0.3s;

      &:hover,
      &.active {
        color: #ff5a6a;
      }
    }
  }
`;

export const StyledHeaderLogo = styled.a`
  position: relative;
  height: 47.42px;
  width: 174px;
  text-decoration: none;
  display: block;

  img {
    position: absolute;
    height: 100%;
    width: 100%;
    left: 0;
    top: 0;
    right: 0;
    bottom: 0;
    object-fit: contain;
    object-position: center;
  }
`;

interface StyledHeaderMenuBarProps {
  active: boolean;
}

export const StyledHeaderMenuBar = styled.button<StyledHeaderMenuBarProps>`
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  border: none;
  background-color: #eef1f3;
  border-radius: 8px;
  padding: 8px;
  transition: background-color 0.3s;

  &:hover {
    background-color: #e0e4e7;
  }

  img,
  svg {
    width: 24px;
    height: 24px;
  }

  @media (min-width: 1024px) {
    display: none;
  }
`;

interface StyledHeaderButtonProps {
  primary?: boolean;
}

export const StyledHeaderButton = styled.a<StyledHeaderButtonProps>`
  display: block;
  padding: 10px 16px;
  font-family: Nunito, "sans-serif";
  font-size: 14px;
  font-weight: 800;
  border-radius: 9999px;
  text-decoration: none;
  line-height: 18px;
  letter-spacing: normal;
  cursor: pointer;
  transition-duration: 0.3s;
  text-align: center;
  box-sizing: border-box;
  box-shadow: 0px 1px 2px 0px rgba(14, 24, 41, 0.05);

  background-color: ${({ primary }) =>
    primary ? "rgb(255, 90, 106)" : "rgba(0, 0, 0, 0)"};
  color: ${({ primary }) =>
    primary ? "rgb(255, 255, 255)" : "rgb(32, 33, 36)"} !important;
  border: 1px solid
    ${({ primary }) => (primary ? "rgb(255, 90, 106)" : "rgb(60, 64, 67)")};

  &:hover {
    background-color: ${({ primary }) =>
      primary ? "rgb(229, 73, 88)" : "rgb(60, 64, 67)"};
    border-color: ${({ primary }) =>
      primary ? "rgb(229, 73, 88)" : "rgb(60, 64, 67)"};
    color: rgb(255, 255, 255) !important;
  }

  @media (max-width: 1023px) {
    padding: 12px 20px;
  }
`;

export const StyledLanguageSelector = styled.button`
  display: flex;
  align-items: center;
  gap: 6px;
  cursor: pointer;
  background-color: #fff;
  border: none;
  border-radius: 9999px;
  padding: 8px 11px 8px 8px;
  transition: background-color 0.3s;
  flex-shrink: 0;

  &:hover {
    background-color: #f8f9fa;
  }

  img,
  svg {
    width: 25px;
    height: 25px;
  }

  span {
    font-size: 14px;
    font-weight: 700;
    color: #202124;
  }
`;

export const StyledMobileActions = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-left: auto;
  flex-shrink: 0;

  @media (min-width: 1024px) {
    display: none;
  }
`;

interface StyledLanguageToggleProps {
  $variant?: "compact" | "menu";
}

export const StyledLanguageToggle = styled.div<StyledLanguageToggleProps>`
  display: flex;
  align-items: center;
  background-color: #eef1f3;
  border-radius: 9999px;
  padding: 3px;
  flex-shrink: 0;

  ${({ $variant }) =>
    $variant === "menu" &&
    `
    width: 100%;
    padding: 4px;
  `}
`;

interface StyledLanguageToggleButtonProps {
  $active?: boolean;
  $variant?: "compact" | "menu";
}

export const StyledLanguageToggleButton = styled.button<StyledLanguageToggleButtonProps>`
  flex: ${({ $variant }) => ($variant === "menu" ? 1 : "none")};
  border: none;
  background-color: ${({ $active }) => ($active ? "#fff" : "transparent")};
  color: ${({ $active }) => ($active ? "#202124" : "#5f6368")};
  font-family: Nunito, "sans-serif";
  font-size: ${({ $variant }) => ($variant === "menu" ? "15px" : "13px")};
  font-weight: ${({ $active }) => ($active ? 800 : 700)};
  line-height: 18px;
  padding: ${({ $variant }) => ($variant === "menu" ? "10px 16px" : "6px 10px")};
  border-radius: 9999px;
  cursor: pointer;
  box-shadow: ${({ $active }) =>
    $active ? "0px 1px 2px 0px rgba(14, 24, 41, 0.08)" : "none"};
  transition: background-color 0.2s, color 0.2s, box-shadow 0.2s;
  min-width: ${({ $variant }) => ($variant === "menu" ? "0" : "40px")};

  &:hover {
    color: #202124;
  }
`;

export const StyledHeaderButtons = styled.div`
  display: none;
  gap: 14px;
  align-items: center;

  @media (min-width: 1024px) {
    display: inline-flex;
  }
`;

export const StyledButtonGroup = styled.div`
  display: inline-flex;
  gap: 9px;
`;

export const StyledMobileButtons = styled.div`
  display: none;

  @media (max-width: 1023px) {
    display: flex;
    flex-direction: column;
    gap: 16px;
    margin-top: 40px;
    width: calc(100% - 4px);
    margin-left: 2px;
    margin-bottom: 2px;

    a {
      width: 100%;
      text-align: center;
      padding: 12px 20px;
    }
  }
`;

export const PageWrapper = styled.div`
  /* display: flex;
    flex-direction: column;
    height: 100%; */
  /* background: url('/images/bg.jpg') center center no-repeat;
    background-size: cover; */
`;

export const PageContainer = styled.div`
  /* flex: 1; */
`;
